import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mutate as revalidateSwr } from 'swr';
import { useApiServices } from '@shared/lib/use-api-services';
import { useAuth } from '@entities/auth';
import { isSessionBoundToUser } from '@shared/api/client/session-identity';
import { readApiError } from '@shared/api/errors/read-api-error';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import type { WithdrawalUserDto, WithdrawalWalletKind } from '@shared/api/contracts';
import type { SecondFactor } from '@shared/api/contracts/security';
import { createIdempotencyKey } from '@entities/withdrawal';
import { isCabinetBalanceOrWithdrawalKey } from './lib/revalidate-withdrawal-lists';
import {
  clearIntentSession,
  readIntentSession,
  writeIntentSession,
  type WithdrawalIntentSession,
} from './lib/intent-session';

export type WithdrawalType = 'merchants' | 'partners';

const walletKindOf = (type: WithdrawalType): WithdrawalWalletKind => (type === 'partners' ? 'partner' : 'merchant');

export interface CreateManualWithdrawalInput {
  network: string;
  address: string;
  amount: string;
  secondFactor: SecondFactor;
  session?: WithdrawalIntentSession | null;
}

type PreparedIntent = {
  success: true;
  intentId: string;
  idempotencyKey: string;
};

export const useWithdrawal = (type: WithdrawalType = 'merchants') => {
  const { t } = useTranslation('cabinet');
  const services = useApiServices();
  const { user, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userId = user?.id != null ? String(user.id) : undefined;

  const fail = async (err: unknown): Promise<{ success: false; error: string }> => {
    const parsed = readApiError(err);
    if (parsed?.code === 'OTP_REQUIRED' || parsed?.code === 'OTP_INVALID') {
      throw err;
    }
    if (parsed?.code === 'WITHDRAWALS_LOCKED') {
      await refreshUser();
    }
    const message = getApiErrorMessage(err, t, 'withdrawal.modal.error');
    setError(message);
    return { success: false as const, error: message };
  };

  /** Перед денежным шагом: токен в хранилище должен принадлежать загруженному профилю. */
  const rejectForeignSession = (): { success: false; error: string } | null => {
    if (isSessionBoundToUser(userId)) return null;
    const message = t('withdrawal.modal.sessionChanged');
    setError(message);
    window.setTimeout(() => window.location.reload(), 1500);
    return { success: false, error: message };
  };

  const prepareIntent = async (
    input: Omit<CreateManualWithdrawalInput, 'secondFactor'>,
  ): Promise<PreparedIntent | { success: false; error: string }> => {
    const foreign = rejectForeignSession();
    if (foreign) return foreign;
    setIsLoading(true);
    setError(null);
    const walletKind = walletKindOf(type);
    const restored =
      input.session && input.session.userId === userId && input.session.walletKind === walletKind
        ? input.session
        : null;
    const sameParams =
      restored &&
      restored.network === input.network &&
      restored.address === input.address &&
      restored.amount === input.amount;
    const idempotencyKey = sameParams ? restored.idempotencyKey : createIdempotencyKey();
    const session: WithdrawalIntentSession = {
      userId: userId ?? '',
      walletKind,
      idempotencyKey,
      intentId: sameParams ? restored.intentId : undefined,
      network: input.network,
      address: input.address,
      amount: input.amount,
    };
    if (userId) writeIntentSession(session);

    try {
      const intent = session.intentId
        ? { intentId: session.intentId }
        : await services.withdrawals.createIntent(
            {
              walletKind,
              assetCode: 'USDT',
              network: input.network,
              address: input.address,
              amount: input.amount,
            },
            idempotencyKey,
          );
      if (!intent.intentId) {
        const message = t('withdrawal.modal.error');
        setError(message);
        return { success: false, error: message };
      }
      if (userId) writeIntentSession({ ...session, intentId: intent.intentId });
      return { success: true, intentId: intent.intentId, idempotencyKey };
    } catch (err: unknown) {
      return fail(err);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmIntent = async (
    prepared: PreparedIntent,
    secondFactor: SecondFactor,
  ): Promise<{ success: true; id: string; status?: string } | { success: false; error: string }> => {
    const foreign = rejectForeignSession();
    if (foreign) return foreign;
    setIsLoading(true);
    setError(null);
    try {
      const confirm =
        type === 'partners'
          ? services.withdrawals.confirmPartnersWithdrawal
          : services.withdrawals.confirmMerchantsWithdrawal;
      const created: WithdrawalUserDto = await confirm(
        { intentId: prepared.intentId, secondFactor },
        prepared.idempotencyKey,
      );
      clearIntentSession();
      await revalidateSwr(isCabinetBalanceOrWithdrawalKey);
      return { success: true as const, id: String(created.id), status: created.status };
    } catch (err: unknown) {
      return fail(err);
    } finally {
      setIsLoading(false);
    }
  };

  const createWithdrawal = async (input: CreateManualWithdrawalInput) => {
    const prepared = await prepareIntent(input);
    if (!prepared.success) return prepared;
    return confirmIntent(prepared, input.secondFactor);
  };

  return {
    createWithdrawal,
    prepareIntent,
    confirmIntent,
    sendIntentEmail: (intentId: string) => services.withdrawals.sendIntentEmailOtp(intentId),
    pendingSession: readIntentSession(userId),
    isLoading,
    error,
  };
};
